import { useState } from 'react';
import styled from 'styled-components';
import { Link, useParams } from 'react-router-dom';
import {
  useListPtoRequestsQuery,
  useCreatePtoRequestMutation,
  useSetPtoRequestStatusMutation,
  useLazyExportPtoRequestsXlsxQuery,
  type PtoRequest,
} from '@shared/api';
import { useAgentPermission } from '@entities/user';
import { Button } from '@shared/ui/Button';
import { Input } from '@shared/ui/Input';

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing(3)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const Columns = styled.div`
  display: grid;
  grid-template-columns: minmax(300px, 380px) 1fr;
  gap: ${({ theme }) => theme.spacing(5)};

  @media (max-width: 880px) {
    grid-template-columns: 1fr;
  }
`;

const Panel = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  padding: ${({ theme }) => theme.spacing(4)};
`;

const Muted = styled.div`
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: 14px;
`;

const Field = styled.label`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1.5)};
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-bottom: ${({ theme }) => theme.spacing(3)};
`;

const Select = styled.select`
  padding: 8px 10px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  font: inherit;
`;

const ReqRow = styled.div`
  padding: ${({ theme }) => theme.spacing(3)} 0;
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const Tag = styled.span`
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.primarySoft};
  color: ${({ theme }) => theme.colors.primary};
`;

const CATEGORIES: Array<{ value: string; label: string }> = [
  { value: 'lab', label: 'Лаборатория' },
  { value: 'lnk', label: 'ЛНК (неразрушающий контроль)' },
  { value: 'mto', label: 'МТО (снабжение)' },
  { value: 'zhvk', label: 'ЖВК (входной контроль)' },
  { value: 'geo', label: 'Геодезия' },
];

function categoryLabel(c: string): string {
  return CATEGORIES.find((x) => x.value === c)?.label ?? c;
}

function statusLabel(s: string): string {
  switch (s) {
    case 'open':
      return 'Открыта';
    case 'in_progress':
      return 'В работе';
    case 'closed':
      return 'Закрыта';
    default:
      return s;
  }
}

export function PtoRequests() {
  const { projectId = '' } = useParams();
  const { can_write } = useAgentPermission('pto');
  const { data, isLoading } = useListPtoRequestsQuery(projectId, { skip: !projectId });
  const [create, { isLoading: saving }] = useCreatePtoRequestMutation();
  const [setStatus] = useSetPtoRequestStatusMutation();
  const [exportXlsx, { isFetching: exporting }] = useLazyExportPtoRequestsXlsxQuery();

  const [category, setCategory] = useState('lab');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [responsible, setResponsible] = useState('');
  const [error, setError] = useState<string | null>(null);

  const requests: PtoRequest[] = data?.items ?? [];
  const functionRequests = data?.function_requests ?? [];
  const inspectionRequests = data?.inspection_requests ?? [];

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!subject.trim()) {
      setError('Укажите тему заявки.');
      return;
    }
    try {
      await create({
        projectId,
        category,
        subject: subject.trim(),
        body: body.trim() || undefined,
        due_date: dueDate.trim() || undefined,
        responsible: responsible.trim() || undefined,
      }).unwrap();
      setSubject('');
      setBody('');
      setDueDate('');
      setResponsible('');
    } catch {
      setError('Не удалось создать заявку.');
    }
  }

  async function onExport() {
    try {
      const blob = await exportXlsx(projectId).unwrap();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pto_requests_${projectId}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      setError('Не удалось выгрузить реестр XLSX.');
    }
  }

  return (
    <div>
      <Row>
        <div>
          <h1 style={{ margin: 0, fontSize: 22 }}>Заявки и реестры</h1>
          <Muted>Лаборатории · ЛНК · МТО · ЖВК · геодезия · освидетельствования с площадок</Muted>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <Button $variant="primary" $size="sm" disabled={exporting} onClick={onExport}>
            {exporting ? 'Формирование…' : 'Реестр XLSX'}
          </Button>
          <Link to={`/agents/pto/${projectId}`}>
            <Button $variant="secondary">← К проекту</Button>
          </Link>
        </div>
      </Row>

      <Columns>
        <div>
          <h3 style={{ marginTop: 0 }}>Новая заявка</h3>
          {can_write ? (
            <Panel as="form" onSubmit={onSubmit}>
              <Field>
                Вид заявки
                <Select value={category} onChange={(e) => setCategory(e.target.value)}>
                  {CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>
                      {c.label}
                    </option>
                  ))}
                </Select>
                <Muted style={{ fontSize: 12 }}>
                  Заявки в Геодезию, МТО, Лабораторию и ЛНК автоматически передаются в сквозной
                  межфункциональный контур смежной службы.
                </Muted>
              </Field>
              <Field>
                Тема
                <Input
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Испытание бетона B25"
                />
              </Field>
              <Field>
                Описание (опц.)
                <Input
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Отбор проб на участке 1"
                />
              </Field>
              <Field>
                Срок (опц., ГГГГ-ММ-ДД)
                <Input
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  placeholder="2026-06-20"
                />
              </Field>
              <Field>
                Ответственный (опц.)
                <Input
                  value={responsible}
                  onChange={(e) => setResponsible(e.target.value)}
                  placeholder="Иванов И.И."
                />
              </Field>
              <Button $variant="primary" type="submit" disabled={saving || !subject.trim()}>
                {saving ? 'Создание…' : 'Создать заявку'}
              </Button>
              {error && <Muted style={{ color: 'crimson', marginTop: 8 }}>{error}</Muted>}
            </Panel>
          ) : (
            <Muted>Недостаточно прав для создания заявок.</Muted>
          )}
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div>
            <h3 style={{ marginTop: 0 }}>Внутренние заявки ПТО ({requests.length})</h3>
            {isLoading ? (
              <Muted>Загрузка…</Muted>
            ) : requests.length === 0 ? (
              <Muted>Внутренних заявок ПТО пока нет.</Muted>
            ) : (
              <Panel>
                {requests.map((r) => (
                  <ReqRow key={r.id}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <Tag>{categoryLabel(r.kind)}</Tag>
                      <span style={{ fontWeight: 600 }}>{r.subject}</span>
                      <Muted style={{ fontSize: 12, marginLeft: 'auto' }}>
                        {statusLabel(r.status)}
                      </Muted>
                    </div>
                    {r.body && <div style={{ marginTop: 4 }}>{r.body}</div>}
                    <Muted style={{ fontSize: 12, marginTop: 4 }}>
                      {r.raw?.due_date ? `Срок: ${r.raw.due_date}` : ''}
                      {r.raw?.responsible ? ` · Отв.: ${r.raw.responsible}` : ''}
                      {` · создано ${new Date(r.created_at).toLocaleDateString('ru-RU')}`}
                    </Muted>
                    {can_write && r.status !== 'closed' && (
                      <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
                        {r.status === 'open' && (
                          <Button
                            $variant="ghost"
                            $size="sm"
                            onClick={() => setStatus({ id: r.id, status: 'in_progress' })}
                          >
                            В работу
                          </Button>
                        )}
                        <Button
                          $variant="ghost"
                          $size="sm"
                          onClick={() => setStatus({ id: r.id, status: 'closed' })}
                        >
                          Закрыть
                        </Button>
                      </div>
                    )}
                  </ReqRow>
                ))}
              </Panel>
            )}
          </div>

          <div>
            <h3 style={{ marginTop: 0 }}>
              Межфункциональные заявки проекта ({functionRequests.length})
            </h3>
            {functionRequests.length === 0 ? (
              <Muted>Межфункциональных заявок по проекту пока нет.</Muted>
            ) : (
              <Panel>
                {functionRequests.map((fr) => (
                  <ReqRow key={fr.id}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <Tag>{fr.request_type}</Tag>
                      <Link
                        to={`/requests/${fr.id}`}
                        style={{ fontWeight: 600, color: 'inherit', textDecoration: 'underline' }}
                      >
                        {fr.title}
                      </Link>
                      <Muted style={{ fontSize: 12, marginLeft: 'auto' }}>
                        {fr.source_function} → {fr.target_function} · {fr.status}
                      </Muted>
                    </div>
                    <Muted style={{ fontSize: 12, marginTop: 4 }}>
                      {fr.due_date ? `Срок: ${fr.due_date} · ` : ''}
                      создано {new Date(fr.created_at).toLocaleDateString('ru-RU')}
                    </Muted>
                  </ReqRow>
                ))}
              </Panel>
            )}
          </div>

          <div>
            <h3 style={{ marginTop: 0 }}>
              Заявки на освидетельствование с площадок ({inspectionRequests.length})
            </h3>
            {inspectionRequests.length === 0 ? (
              <Muted>Заявок на освидетельствование с площадок проекта пока нет.</Muted>
            ) : (
              <Panel>
                {inspectionRequests.map((ir) => (
                  <ReqRow key={ir.id}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <Tag>освидетельствование</Tag>
                      <Link
                        to={`/agents/field/${ir.site_id}`}
                        style={{ fontWeight: 600, color: 'inherit', textDecoration: 'underline' }}
                      >
                        {ir.title}
                      </Link>
                      {ir.executive_act_id && !ir.act_deleted && <Tag>АОСР собран</Tag>}
                      <Muted style={{ fontSize: 12, marginLeft: 'auto' }}>
                        {ir.status}
                        {ir.decision ? ` · решение: ${ir.decision}` : ''}
                      </Muted>
                    </div>
                    {ir.note && <div style={{ marginTop: 4 }}>{ir.note}</div>}
                    <Muted style={{ fontSize: 12, marginTop: 4 }}>
                      {ir.period_start
                        ? `Период: ${ir.period_start}${ir.period_end ? ` — ${ir.period_end}` : ''} · `
                        : ''}
                      создано {new Date(ir.created_at).toLocaleDateString('ru-RU')}
                    </Muted>
                  </ReqRow>
                ))}
              </Panel>
            )}
          </div>
        </div>
      </Columns>
    </div>
  );
}
